import { useState, useEffect } from 'react';
import {
  useTranslation,
  useDispatch,
  useSelector,
  useForm,
  Controller,
  zodResolver,
  z,
  type UseFormReset,
  useIsRTL,
} from '@spcc/core';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  Input,
  Separator,
  cn,
  useToast,
} from '@spcc/uikit';
import {
  Scale,
  Truck,
  CheckCircle,
  XCircle,
  Gauge,
  Loader2,
  PlayCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  weighbridgeActions,
  selectWeighbridgeLoading,
  selectWeighbridgeError,
  selectFirstWeighTicket,
  selectSecondWeighTicket,
  selectDeliveredSuccess,
  selectStartProcessingResult,
  selectStartProcessingSuccess,
  selectBalanceCheckError,
} from '../../store';
import type { StartProcessingRequest } from '../../api/scan-types';
import type { MarkDeliveredRequest } from '../../api/weighbridge-types';

const START_PROCESSING_VALIDATION = {
  ONE_REQUIRED: 'weighbridge.validation.startProcessingOneRequired',
  ONLY_ONE: 'weighbridge.validation.startProcessingOnlyOne',
} as const;

const startProcessingFormSchema = z
  .object({
    orderNumber: z.string().default(''),
    vehicleLicensePlate: z.string().default(''),
    gateCode: z.string().default(''),
  })
  .superRefine((values, ctx) => {
    const orderNumber = values.orderNumber?.trim();
    const vehicleLicensePlate = values.vehicleLicensePlate?.trim();

    if (!orderNumber && !vehicleLicensePlate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: START_PROCESSING_VALIDATION.ONE_REQUIRED,
        path: ['orderNumber'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: START_PROCESSING_VALIDATION.ONE_REQUIRED,
        path: ['vehicleLicensePlate'],
      });
      return;
    }

    if (orderNumber && vehicleLicensePlate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: START_PROCESSING_VALIDATION.ONLY_ONE,
        path: ['orderNumber'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: START_PROCESSING_VALIDATION.ONLY_ONE,
        path: ['vehicleLicensePlate'],
      });
    }
  });

type StartProcessingFormValues = z.infer<typeof startProcessingFormSchema>;

const START_PROCESSING_DEFAULT_VALUES: StartProcessingFormValues = {
  orderNumber: '',
  vehicleLicensePlate: '',
  gateCode: '',
};

type DispatchFn = ReturnType<typeof useDispatch>;

function buildStartProcessingPayload(
  values: StartProcessingFormValues,
): StartProcessingRequest {
  const orderNumber = values.orderNumber.trim();
  const vehicleLicensePlate = values.vehicleLicensePlate.trim();
  const gateCode = values.gateCode.trim();

  const payload: StartProcessingRequest = {};

  if (orderNumber) {
    payload.orderNumber = orderNumber;
  }

  if (vehicleLicensePlate) {
    payload.vehicleLicensePlate = vehicleLicensePlate;
  }

  if (gateCode) {
    payload.gateCode = gateCode;
  }

  return payload;
}

function submitStartProcessing(
  dispatch: DispatchFn,
  values: StartProcessingFormValues,
) {
  dispatch(
    weighbridgeActions.startProcessing(buildStartProcessingPayload(values)),
  );
}

function resetStartProcessingForm(
  dispatch: DispatchFn,
  reset: UseFormReset<StartProcessingFormValues>,
) {
  reset(START_PROCESSING_DEFAULT_VALUES);
  dispatch(weighbridgeActions.resetStartProcessing());
}

export function KioskWeighbridgeSection() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const isRTL = useIsRTL();
  const loading = useSelector(selectWeighbridgeLoading);
  const error = useSelector(selectWeighbridgeError);
  const firstTicket = useSelector(selectFirstWeighTicket);
  const secondTicket = useSelector(selectSecondWeighTicket);
  const deliveredSuccess = useSelector(selectDeliveredSuccess);
  const startProcessingResult = useSelector(selectStartProcessingResult);
  const startProcessingSuccess = useSelector(selectStartProcessingSuccess);
  const balanceCheckError = useSelector(selectBalanceCheckError);

  const [activeTab, setActiveTab] = useState('start-processing');

  // Local state for inputs
  const [firstOrderNumber, setFirstOrderNumber] = useState('');
  const [tareWeight, setTareWeight] = useState('');

  const [secondOrderNumber, setSecondOrderNumber] = useState('');
  const [grossWeight, setGrossWeight] = useState('');

  const [deliveredOrderNumber, setDeliveredOrderNumber] = useState('');
  const [exitGateCode, setExitGateCode] = useState('');

  const {
    control: startProcessingControl,
    handleSubmit: handleStartProcessingSubmit,
    reset: resetStartProcessing,
    formState: { errors: startProcessingErrors },
  } = useForm<StartProcessingFormValues>({
    resolver: zodResolver(startProcessingFormSchema) as any,
    defaultValues: START_PROCESSING_DEFAULT_VALUES,
  });

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: t('common.error', 'Error'),
        description: error,
      });
    }
  }, [error, toast, t]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(weighbridgeActions.resetAll());
    };
  }, [dispatch]);

  const handleFirstWeigh = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(
      weighbridgeActions.firstWeigh({
        orderNumber: firstOrderNumber,
        tareWeight: parseFloat(tareWeight),
      }),
    );
  };

  const handleSecondWeigh = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(
      weighbridgeActions.secondWeigh({
        orderNumber: secondOrderNumber,
        grossWeight: parseFloat(grossWeight),
      }),
    );
  };

  const handleMarkDelivered = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: MarkDeliveredRequest = { orderNumber: deliveredOrderNumber };
    if (exitGateCode.trim()) {
      payload.gateCode = exitGateCode.trim();
    }
    dispatch(weighbridgeActions.markDelivered(payload));
  };

  const handleStartProcessing = (values: StartProcessingFormValues) => {
    submitStartProcessing(dispatch, values);
  };

  const handleStartProcessingReset = () => {
    resetStartProcessingForm(dispatch, resetStartProcessing);
  };

  return (
    <div
      className="container mx-auto p-4 md:p-6 lg:p-8 max-w-5xl"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Gauge className="w-10 h-10 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-foreground">
              {t('weighbridge.title', 'Weighbridge Operations')}
            </h1>
            <p className="text-muted-foreground text-lg mt-1">
              {t('weighbridge.subtitle', 'Record vehicle weights')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <TabsList
          className={cn('grid w-full grid-cols-4', isRTL && 'direction-rtl')}
        >
          <TabsTrigger
            value="start-processing"
            className="flex items-center gap-2"
          >
            <PlayCircle className="w-4 h-4" />
            <span>{t('weighbridge.startProcessing', 'Start Processing')}</span>
          </TabsTrigger>
          <TabsTrigger value="first-weigh" className="flex items-center gap-2">
            <Scale className="w-4 h-4" />
            <span>{t('weighbridge.firstWeigh', 'First Weigh')}</span>
          </TabsTrigger>
          <TabsTrigger value="second-weigh" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            <span>{t('weighbridge.secondWeigh', 'Second Weigh')}</span>
          </TabsTrigger>
          <TabsTrigger
            value="mark-delivered"
            className="flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            <span>{t('weighbridge.markDelivered', 'Mark Delivered')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Start Processing Tab */}
        <TabsContent value="start-processing">
          <Card className="glass-card border-border/50">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center justify-between">
                {t('weighbridge.startProcessing', 'Start Processing')}
                {startProcessingSuccess && startProcessingResult && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStartProcessingReset}
                    className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                    {t('common.reset', 'Reset')}
                  </Button>
                )}
              </CardTitle>
              <CardDescription className="text-base">
                {t(
                  'weighbridge.startProcessingDesc',
                  'Start processing by order number or vehicle license plate',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Balance Check Error Warning */}
              {balanceCheckError && !balanceCheckError.allowed && (
                <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                      <p className="font-semibold text-amber-700 dark:text-amber-400">
                        {t(
                          'weighbridge.insufficientBalance',
                          'Insufficient Balance',
                        )}
                      </p>
                      <div className="text-sm text-amber-600 dark:text-amber-300 space-y-1">
                        <p>
                          {t(
                            'weighbridge.balanceDetails.chargeAmount',
                            'Estimated Charge',
                          )}
                          :{' '}
                          <span className="font-mono font-medium">
                            {balanceCheckError.chargeAmount.toLocaleString(
                              'en-SA',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}{' '}
                            SAR
                          </span>
                        </p>
                        <p>
                          {t(
                            'weighbridge.balanceDetails.availableBalance',
                            'Available Balance',
                          )}
                          :{' '}
                          <span className="font-mono font-medium">
                            {balanceCheckError.availableBalance.toLocaleString(
                              'en-SA',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}{' '}
                            SAR
                          </span>
                        </p>
                        <p className="text-amber-700 dark:text-amber-200 font-semibold">
                          {t(
                            'weighbridge.balanceDetails.shortfall',
                            'Shortfall',
                          )}
                          :{' '}
                          <span className="font-mono">
                            {balanceCheckError.shortfall.toLocaleString(
                              'en-SA',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}{' '}
                            SAR
                          </span>
                        </p>
                      </div>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        {t(
                          'weighbridge.balanceDetails.hint',
                          'Please contact finance to increase credit limit or make a payment before proceeding.',
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!startProcessingResult ? (
                <form
                  onSubmit={handleStartProcessingSubmit(handleStartProcessing)}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.orderNumber', 'Order Number')}
                    </label>
                    <Controller
                      name="orderNumber"
                      control={startProcessingControl}
                      render={({ field }) => {
                        const { ref: _ref, value, ...fieldProps } = field;
                        return (
                          <Input
                            value={value ?? ''}
                            placeholder={t(
                              'weighbridge.orderPlaceholder',
                              'Enter order number (e.g., SP00XX)',
                            )}
                            disabled={loading}
                            className={cn(
                              'h-12 text-base font-mono',
                              startProcessingErrors.orderNumber &&
                                'border-destructive',
                            )}
                            {...fieldProps}
                          />
                        );
                      }}
                    />
                    {startProcessingErrors.orderNumber && (
                      <p className="text-xs text-destructive">
                        {t(startProcessingErrors.orderNumber.message || '')}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t(
                        'weighbridge.vehicleLicensePlate',
                        'Vehicle License Plate',
                      )}
                    </label>
                    <Controller
                      name="vehicleLicensePlate"
                      control={startProcessingControl}
                      render={({ field }) => {
                        const { ref: _ref, value, ...fieldProps } = field;
                        return (
                          <Input
                            value={value ?? ''}
                            placeholder={t(
                              'weighbridge.vehicleLicensePlaceholder',
                              'Enter vehicle license plate',
                            )}
                            disabled={loading}
                            className={cn(
                              'h-12 text-base font-mono',
                              startProcessingErrors.vehicleLicensePlate &&
                                'border-destructive',
                            )}
                            {...fieldProps}
                          />
                        );
                      }}
                    />
                    {startProcessingErrors.vehicleLicensePlate && (
                      <p className="text-xs text-destructive">
                        {t(
                          startProcessingErrors.vehicleLicensePlate.message ||
                            '',
                        )}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.entryGateCode', 'Entry Gate Code')}
                    </label>
                    <Controller
                      name="gateCode"
                      control={startProcessingControl}
                      render={({ field }) => {
                        const { ref: _ref, value, ...fieldProps } = field;
                        return (
                          <Input
                            value={value ?? ''}
                            placeholder={t(
                              'weighbridge.entryGateCodePlaceholder',
                              'e.g. GATE-MKFJUTFO-OM',
                            )}
                            disabled={loading}
                            className="h-12 text-base font-mono"
                            {...fieldProps}
                          />
                        );
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'weighbridge.gateCodeHint',
                        'Provide gate code to trigger full SAP flow (SO creation + contract resolution)',
                      )}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2
                          className={cn(
                            'h-5 w-5 animate-spin',
                            isRTL ? 'ml-2' : 'mr-2',
                          )}
                        />
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        <PlayCircle
                          className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')}
                        />
                        {t('common.submit', 'Submit')}
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-semibold text-green-700 dark:text-green-400">
                        {t(
                          'weighbridge.startProcessingSuccess',
                          'Processing started',
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('weighbridge.orderNumber', 'Order Number')}:
                      </span>
                      <span className="font-medium font-mono">
                        {startProcessingResult.order.orderNumber}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t(
                          'weighbridge.startProcessingStatusBefore',
                          'Previous Status',
                        )}
                        :
                      </span>
                      <span className="font-medium">
                        {startProcessingResult.statusUpdate.before ||
                          t('common.notAvailable', 'N/A')}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t(
                          'weighbridge.startProcessingStatusAfter',
                          'Current Status',
                        )}
                        :
                      </span>
                      <span className="font-medium">
                        {startProcessingResult.statusUpdate.after ||
                          t('common.notAvailable', 'N/A')}
                      </span>
                    </div>
                    {/* Balance Info when successful */}
                    {startProcessingResult.balanceCheck && (
                      <>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            {t(
                              'weighbridge.balanceDetails.chargeAmount',
                              'Estimated Charge',
                            )}
                            :
                          </span>
                          <span className="font-medium font-mono">
                            {startProcessingResult.balanceCheck.chargeAmount.toLocaleString(
                              'en-SA',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}{' '}
                            SAR
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            {t(
                              'weighbridge.balanceDetails.availableBalance',
                              'Available Balance',
                            )}
                            :
                          </span>
                          <span className="font-medium font-mono text-green-600 dark:text-green-400">
                            {startProcessingResult.balanceCheck.availableBalance.toLocaleString(
                              'en-SA',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}{' '}
                            SAR
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* First Weigh Tab */}
        <TabsContent value="first-weigh">
          <Card className="glass-card border-border/50">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center justify-between">
                {t('weighbridge.firstWeigh', 'First Weigh')}
                {firstTicket && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      dispatch(weighbridgeActions.resetFirstWeigh());
                      setFirstOrderNumber('');
                      setTareWeight('');
                    }}
                    className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                    {t('common.reset', 'Reset')}
                  </Button>
                )}
              </CardTitle>
              <CardDescription className="text-base">
                {t(
                  'weighbridge.firstWeighDesc',
                  'Record empty vehicle weight (Tare)',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!firstTicket ? (
                <form onSubmit={handleFirstWeigh} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.orderNumber', 'Order Number')}
                    </label>
                    <Input
                      placeholder="SP00XX"
                      value={firstOrderNumber}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => setFirstOrderNumber(e.target.value)}
                      required
                      disabled={loading}
                      className="h-12 text-base font-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.tareWeight', 'Tare Weight')} (kg)
                    </label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={tareWeight}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => setTareWeight(e.target.value)}
                      required
                      disabled={loading}
                      className="h-12 text-base"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base"
                    disabled={loading || !firstOrderNumber || !tareWeight}
                  >
                    {loading ? (
                      <>
                        <Loader2
                          className={cn(
                            'h-5 w-5 animate-spin',
                            isRTL ? 'ml-2' : 'mr-2',
                          )}
                        />
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        <Scale
                          className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')}
                        />
                        {t('common.submit', 'Submit')}
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-semibold text-green-700 dark:text-green-400">
                        {t(
                          'weighbridge.successFirstWeigh',
                          'First weigh recorded successfully',
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('weighbridge.orderNumber', 'Order Number')}:
                      </span>
                      <span className="font-medium font-mono">
                        {firstTicket.orderNumber}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('weighbridge.tareWeight', 'Tare Weight')}:
                      </span>
                      <span className="font-medium text-lg">
                        {firstTicket.tareWeight?.toLocaleString()} kg
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('common.date', 'Date')}:
                      </span>
                      <span className="font-medium text-sm">
                        {new Date(firstTicket.measuredAt).toLocaleString(
                          isRTL ? 'ar-SA' : 'en-US',
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Second Weigh Tab */}
        <TabsContent value="second-weigh">
          <Card className="glass-card border-border/50">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center justify-between">
                {t('weighbridge.secondWeigh', 'Second Weigh')}
                {secondTicket && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      dispatch(weighbridgeActions.resetSecondWeigh());
                      setSecondOrderNumber('');
                      setGrossWeight('');
                    }}
                    className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                    {t('common.reset', 'Reset')}
                  </Button>
                )}
              </CardTitle>
              <CardDescription className="text-base">
                {t(
                  'weighbridge.secondWeighDesc',
                  'Record loaded vehicle weight (Gross)',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!secondTicket ? (
                <form onSubmit={handleSecondWeigh} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.orderNumber', 'Order Number')}
                    </label>
                    <Input
                      placeholder="SP00XX"
                      value={secondOrderNumber}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => setSecondOrderNumber(e.target.value)}
                      required
                      disabled={loading}
                      className="h-12 text-base font-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.grossWeight', 'Gross Weight')} (kg)
                    </label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={grossWeight}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => setGrossWeight(e.target.value)}
                      required
                      disabled={loading}
                      className="h-12 text-base"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base"
                    disabled={loading || !secondOrderNumber || !grossWeight}
                  >
                    {loading ? (
                      <>
                        <Loader2
                          className={cn(
                            'h-5 w-5 animate-spin',
                            isRTL ? 'ml-2' : 'mr-2',
                          )}
                        />
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        <Truck
                          className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')}
                        />
                        {t('common.submit', 'Submit')}
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-semibold text-green-700 dark:text-green-400">
                        {t(
                          'weighbridge.successSecondWeigh',
                          'Second weigh recorded successfully',
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('weighbridge.orderNumber', 'Order Number')}:
                      </span>
                      <span className="font-medium font-mono">
                        {secondTicket.orderNumber}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('weighbridge.grossWeight', 'Gross Weight')}:
                      </span>
                      <span className="font-medium text-lg">
                        {secondTicket.grossWeight?.toLocaleString()} kg
                      </span>
                    </div>
                    <Separator />
                    {secondTicket.netWeight && (
                      <>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10">
                          <span className="text-sm font-semibold text-primary">
                            {t('weighbridge.netWeight', 'Net Weight')}:
                          </span>
                          <span className="font-bold text-xl text-primary">
                            {secondTicket.netWeight.toLocaleString()} kg
                          </span>
                        </div>
                        <Separator />
                      </>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('common.date', 'Date')}:
                      </span>
                      <span className="font-medium text-sm">
                        {new Date(secondTicket.measuredAt).toLocaleString(
                          isRTL ? 'ar-SA' : 'en-US',
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mark Delivered Tab */}
        <TabsContent value="mark-delivered">
          <Card className="glass-card border-border/50">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center justify-between">
                {t('weighbridge.markDelivered', 'Mark Delivered')}
                {deliveredSuccess && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      dispatch(weighbridgeActions.resetDelivered());
                      setDeliveredOrderNumber('');
                      setExitGateCode('');
                    }}
                    className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                    {t('common.reset', 'Reset')}
                  </Button>
                )}
              </CardTitle>
              <CardDescription className="text-base">
                {t(
                  'weighbridge.markDeliveredDesc',
                  'Mark shipment as delivered at destination',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!deliveredSuccess ? (
                <form onSubmit={handleMarkDelivered} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.orderNumber', 'Order Number')}
                    </label>
                    <Input
                      placeholder="SP00XX"
                      value={deliveredOrderNumber}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => setDeliveredOrderNumber(e.target.value)}
                      required
                      disabled={loading}
                      className="h-12 text-base font-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium block">
                      {t('weighbridge.exitGateCode', 'Exit Gate Code')}
                    </label>
                    <Input
                      placeholder={t(
                        'weighbridge.exitGateCodePlaceholder',
                        'e.g. GATE-MKFJUTFO-OM',
                      )}
                      value={exitGateCode}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => setExitGateCode(e.target.value)}
                      disabled={loading}
                      className="h-12 text-base font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'weighbridge.exitGateCodeHint',
                        'Provide gate code to trigger full SAP exit processing',
                      )}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base"
                    disabled={loading || !deliveredOrderNumber}
                  >
                    {loading ? (
                      <>
                        <Loader2
                          className={cn(
                            'h-5 w-5 animate-spin',
                            isRTL ? 'ml-2' : 'mr-2',
                          )}
                        />
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        <CheckCircle
                          className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')}
                        />
                        {t('common.submit', 'Submit')}
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="p-6 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-green-700 dark:text-green-400 mb-2">
                      {t(
                        'weighbridge.successDelivered',
                        'Shipment marked as delivered',
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('weighbridge.orderNumber', 'Order Number')}
                    </p>
                    <p className="font-mono text-lg font-medium">
                      {deliveredOrderNumber}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
